import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type CallbackConfig, type WebhookDelivery } from '../../lib/api';
import { EditScope, Field, SettingsCard, StaticValue, TextInput } from '@allrouted/ui/Field';
import { IconButton, RefreshIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * Outbound callbacks.
 *
 * There was no screen for this at all — a callback could only be registered
 * through the API, which is fine for the integration that consumes it and no
 * help whatsoever when somebody has to answer "did the event go out?". That
 * question is nearly always the first one asked when an integration looks
 * broken, and the answer was in a table nobody could reach.
 *
 * Registration deliberately sends a test delivery before saving, so a URL that
 * cannot receive fails here, in front of the person who typed it, rather than
 * silently at three in the morning.
 */

const EVENTS = [
  'ordersChanged',
  'customersChanged',
  'techniciansChanged',
  'driversChanged',
  'vehiclesChanged',
  'depotsChanged',
  'regionsChanged',
  'territoryChanged',
  'routesChanged',
  'executionEvent',
  'optimizationCompleted',
];

export function WebhooksPage({ territoryId }: { territoryId: string | null }) {
  const ui = useUi();
  const [config, setConfig] = useState<CallbackConfig | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [password, setPassword] = useState('');

  const load = useCallback(async () => {
    if (!territoryId) return;
    setLoading(true);
    try {
      /*
       * A territory with no callback answers 404, which is correct for the API
       * and is not an error here -- "none registered" is the ordinary starting
       * state of this screen, so it must not take the delivery list down with
       * it.
       */
      const [c, d] = await Promise.all([
        api.callback(territoryId).catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
        api.webhookDeliveries(territoryId),
      ]);
      setConfig(c);
      setDeliveries(d.deliveries);
      setUrl(c?.url ?? '');
      /*
       * The password is never returned, only whether one is set, so the field
       * starts empty. Saving it empty leaves the existing one alone rather than
       * clearing it — see the note beside the field.
       */
      setPassword('');
    } catch (err) {
      ui.notify(
        'error',
        'Could not load callbacks',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setLoading(false);
    }
  }, [territoryId, ui]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!territoryId) return;
    setSaving(true);
    try {
      await api.saveCallback(territoryId, {
        url: url.trim(),
        ...(password ? { signaturePassword: password } : {}),
        test: true,
      });
      ui.notify('ok', 'Callback registered', 'A test delivery was accepted by that URL.');
      setEditing(false);
      await load();
    } catch (err) {
      /*
       * Registration failing is the useful case, not the annoying one: the URL
       * did not accept a test, so it would not have accepted anything.
       */
      ui.notify(
        'error',
        'That URL did not accept a test delivery',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!territoryId) return;
    const ok = await ui.confirm({
      title: 'Stop sending callbacks?',
      body: 'Nothing will be delivered until a URL is registered again. Events that happen in the meantime are not kept.',
    });
    if (!ok) return;
    try {
      await api.deleteCallback(territoryId);
      ui.notify('ok', 'Callbacks stopped');
      setEditing(false);
      await load();
    } catch (err) {
      ui.notify('error', 'Could not remove', err instanceof ApiError ? err.message : String(err));
    }
  };

  const retry = async (deliveryId: string) => {
    if (!territoryId) return;
    setRetrying(deliveryId);
    try {
      await api.retryWebhookDelivery(territoryId, deliveryId);
      ui.notify('ok', 'Queued again', 'The same payload is on its way.');
      await load();
    } catch (err) {
      ui.notify('error', 'Could not retry', err instanceof ApiError ? err.message : String(err));
    } finally {
      setRetrying(null);
    }
  };

  if (!territoryId) return <div className="empty">Select a territory.</div>;

  return (
    <>
      <SettingsCard
        title="Callback"
        description="One URL per territory receives every event as a POST. This is the same callback the compatibility API registers, so an existing listener needs no changes."
        editing={editing}
        dirty={editing && (url.trim() !== (config?.url ?? '') || password !== '')}
        saving={saving}
        onEdit={() => setEditing(true)}
        onCancel={() => {
          setEditing(false);
          setUrl(config?.url ?? '');
          setPassword('');
        }}
        onSave={() => void save()}
        actions={
          config?.url && !editing ? (
            <button type="button" className="btn btn-danger" onClick={() => void remove()}>
              Stop sending
            </button>
          ) : undefined
        }
      >
        <EditScope editing={editing}>
          <Field label="URL" hint="Must answer 2xx to a test delivery before it is saved" wide>
            <TextInput value={url} onChange={setUrl} placeholder="https://example.com/hooks/allrouted" />
          </Field>
          <Field
            label="Signature password"
            hint={
              config?.signatureConfigured
                ? 'Set. Type a new one to replace it; leaving it empty keeps the current one.'
                : 'Optional. Deliveries are signed HMAC-SHA256 when set.'
            }
            wide
          >
            {editing ? (
              <input
                type="password"
                className="field"
                value={password}
                placeholder={config?.signatureConfigured ? 'Unchanged' : 'Not set'}
                onChange={(e) => setPassword(e.target.value)}
              />
            ) : (
              <StaticValue>{config?.signatureConfigured ? 'Set' : 'Not set'}</StaticValue>
            )}
          </Field>
        </EditScope>
      </SettingsCard>

      <SettingsCard
        title="What is delivered"
        description="Every event goes to the one URL; there is no per-event subscription, because the incumbent has none and a listener written against it filters on the event name."
      >
        <p className="hint">
          Each delivery is <code>{'{ requestId, territoryId, event, data }'}</code>.{' '}
          <code>requestId</code> is the id returned by the API write that caused it, and is
          explicitly <code>null</code> when the change came from the console instead.
        </p>
        <div className="event-list">
          {EVENTS.map((event) => (
            <code key={event} className="event">
              {event}
            </code>
          ))}
        </div>
        <p className="hint">
          Signed deliveries carry <code>X-AllRouted-Signature</code>: HMAC-SHA256 of the raw
          body, hex encoded. Verify against the raw bytes, not re-serialised JSON.
        </p>
        <p className="hint">
          Any 2xx counts as delivered. A failure is retried five times, at 20, 40, 60, 80 and
          100 seconds — the incumbent's schedule, so listeners built against it keep working.
          After that it stops, and you can send it again below.
        </p>
      </SettingsCard>

      <SettingsCard
        title="Recent deliveries"
        editing={false}
        actions={
          <IconButton label="Refresh" onClick={() => void load()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        }
      >
        {deliveries.length === 0 ? (
          <p className="hint">Nothing has been sent yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th className="num">Attempts</th>
                  <th>Result</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id}>
                    <td>{new Date(d.created_at).toLocaleString()}</td>
                    <td className="mono">{d.event}</td>
                    <td>
                      <span className="pill" data-tone={tone(d.status)}>
                        {d.status}
                      </span>
                    </td>
                    <td className="num">{d.attempts}</td>
                    <td className="dim">
                      {d.last_status ? `HTTP ${d.last_status}` : (d.last_error ?? '')}
                    </td>
                    <td>
                      {d.status === 'failed' ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={retrying === d.id}
                          onClick={() => void retry(d.id)}
                        >
                          {retrying === d.id ? 'Sending' : 'Send again'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>
    </>
  );
}

function tone(status: string): string {
  if (status === 'delivered') return 'ok';
  if (status === 'failed') return 'late';
  return 'warn';
}
